import { type FC, useEffect, useState } from "react";
import { Box, Stack, Typography, alpha } from "@mui/material";
import { useDateFormat, useTranslation } from "@/shared/hooks";
import { splitCountdown, type CountdownParts } from "./splitCountdown";

const pad = (n: number) => String(n).padStart(2, "0");

const Tile: FC<{ value: number; label: string; accent?: boolean }> = ({
  value,
  label,
  accent,
}) => (
  <Box
    sx={(theme) => ({
      flex: "1 1 0",
      minWidth: 0,
      maxWidth: 132,
      textAlign: "center",
      py: { xs: 1.5, sm: 2.5 },
      borderRadius: "14px",
      backgroundColor: alpha(theme.palette.background.elevated, 0.85),
      border: `1px solid ${accent ? alpha(theme.palette.primary.main, 0.5) : theme.palette.divider}`,
      backdropFilter: "blur(6px)",
    })}
  >
    <Typography
      variant="h2"
      component="div"
      sx={{
        fontSize: { xs: "1.75rem", sm: "2.6rem", md: "3.25rem" },
        lineHeight: 1,
        fontVariantNumeric: "tabular-nums",
        color: accent ? "primary.light" : "text.primary",
      }}
    >
      {pad(value)}
    </Typography>
    <Typography
      variant="label"
      sx={{
        color: "text.muted",
        mt: { xs: 0.5, sm: 1 },
        fontSize: { xs: "0.6rem", sm: "0.75rem" },
      }}
    >
      {label}
    </Typography>
  </Box>
);

export const StreamCountdown: FC<{ startAt: string }> = ({ startAt }) => {
  const { t } = useTranslation();
  const { formatDateTime } = useDateFormat();
  const target = new Date(startAt).getTime();
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, []);

  const parts: CountdownParts = splitCountdown(target - now);
  const tiles = [
    ...(parts.days > 0 ? [{ key: "days", value: parts.days }] : []),
    { key: "hours", value: parts.hours },
    { key: "minutes", value: parts.minutes },
    { key: "seconds", value: parts.seconds, accent: true },
  ];

  return (
    <Stack
      alignItems="center"
      spacing={{ xs: 2, sm: 3 }}
      sx={{ width: "100%" }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.25}
        sx={(theme) => ({
          px: 2.25,
          py: 1,
          borderRadius: 999,
          backgroundColor: alpha(theme.palette.primary.main, 0.15),
          border: `1px solid ${alpha(theme.palette.primary.main, 0.4)}`,
        })}
      >
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            bgcolor: "primary.light",
          }}
        />
        <Typography
          variant="label"
          sx={{ color: "text.secondary", letterSpacing: "0.16em" }}
        >
          {t("stream.pending.title")}
        </Typography>
      </Stack>

      <Stack
        direction="row"
        spacing={{ xs: 1, sm: 1.75 }}
        sx={{ width: "100%", maxWidth: 600, px: 2, justifyContent: "center" }}
      >
        {tiles.map((tile) => (
          <Tile
            key={tile.key}
            value={tile.value}
            label={t(`stream.countdown.${tile.key}`)}
            accent={tile.accent}
          />
        ))}
      </Stack>

      <Typography variant="overline" sx={{ color: "text.muted" }}>
        {formatDateTime(startAt)}
      </Typography>
    </Stack>
  );
};
